import { Component, DestroyRef, inject, signal, OnInit } from '@angular/core';
import { API, ScreenHardwareDTO, Pagination } from '../../../services/api/api';
import { UtilService } from 'src/app/services/util.service';
import { TranslateService } from '@ngx-translate/core';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { ToasterService, ToastType } from '../../../services/toaster/toaster.service';
import { ErrorService } from 'src/app/services/error.service';
import { SbSplitButton } from '../../../services/global/global-interfaces';
import { PermissionService } from '../../../services/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';
import { ModalNavigationComponent } from '../../../services/gui/modal/modal-navigation-component';
import { ModalNavigationService } from '../../../services/gui/modal/modal-navigation.service';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { BehaviorSubject, Subscription } from 'rxjs';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { RoutingID } from '../../../routes/sb-routing-enum';

type SplitButtonAction = 'edit' | 'delete';

@Component({
  selector: 'sb-page-screen-hardware',
  templateUrl: './screen-hardware.component.html',
  styleUrls: ['./screen-hardware.component.scss'],
  standalone: false,
})
export class ScreenHardwareComponent implements OnInit, ModalNavigationComponent {
  public readonly permissionService = inject(PermissionService);
  public readonly util = inject(UtilService);
  private readonly api = inject(API);
  private readonly modalService = inject(ModalService);
  private readonly toastHandler = inject(ToasterService);
  private readonly errorHandler = inject(ErrorService);
  private readonly translate = inject(TranslateService);
  private readonly modalNavigation = inject(ModalNavigationService);
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);

  // global variables
  loading = signal<boolean>(true);
  debounceTimer: ReturnType<typeof setTimeout> = null;
  subs: Subscription[] = [];
  isInModalContext: boolean;

  // table wrapper variables
  pagination: BehaviorSubject<Pagination> | undefined;
  screenHardware: ScreenHardwareDTO[] = [];

  // filter variables
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  ngOnInit(): void {
    this.loading.set(true);
  }

  requestDebounced(options?: BehaviorSubject<Pagination>) {
    this.loading.set(true);
    if (options) {
      this.pagination = options;
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.subs.forEach(sub => sub.unsubscribe());
    this.subs = [];

    this.debounceTimer = setTimeout(() => {
      this.refreshFromServer();
    }, 500);
  }

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .ca_md_ScreenHardware_retrieveAll(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: data => {
          this.screenHardware = this.buildSplitButtons(data);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  deleteScreen(screenID: number) {
    if (!screenID || screenID < 0) {
      throw new Error('Missing screen ID');
    }
    // show confirm modal
    this.modalService
      .confirm({
        title: this.translate.instant('lang.globals.approve'),
        message: this.translate.instant('lang.devices.deleteScreenHardwareMessage'),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.api
            .ca_md_ScreenHardware_deleteScreenHardware(screenID)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.toastHandler.toast(ToastType.SUCCESS, this.translate.instant('lang.devices.deletedScreenHardware'));

                // refresh table after deletion
                this.refreshFromServer();
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
      })
      .catch(error => {
        this.errorHandler.errorHandler(error);
      });
  }

  selectScreenHardware(hardware: ScreenHardwareDTO) {
    if (this.isInModalContext) {
      this.modalNavigation.result(hardware);
    }
  }

  buildSplitButtons<T extends { id: any; global: boolean }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (!item.global) {
        if (this.permissionService.hasPermissions([this.permissionService.Permission.screen_hardware_edit])) {
          item.sbButtons.push({
            icon: 'edit',
            tooltip: 'lang.globals.edit',
            link: RoutingID.screenHardwareEdit,
            queryParams: { returnTo: this.util.getPageLink() },
            pathParams: [{ id: 'screenHardwareID', value: item?.id }],
          });
        }
        if (this.permissionService.hasPermissions([this.permissionService.Permission.screen_hardware_delete])) {
          item.sbButtons.push({
            icon: 'trash',
            iconClass: 'hover:!tw-bg-sbRed-500 *:tw-text-sbRed-500',
            action: 'delete',
            tooltip: 'lang.globals.delete',
          });
        }
      }
    });
    return result;
  }

  resolveSplitButtonAction(screen: ScreenHardwareDTO, $event: SplitButtonAction) {
    switch ($event) {
      case 'delete':
        this.deleteScreen(screen?.id);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
